import { CartAddEvent } from '@theme/events';

/**
 * <featured-drop>
 *
 * A full-screen "drop of the week" takeover spotlighting one product. Opens on a trigger
 * (scroll depth, exit-intent, or a delay), once per session/day. The card tilts in 3D toward
 * the cursor and decorative layers parallax. Adds the product straight to the cart (mirrors
 * the add flow in assets/product-bundle.js) and opens the drawer. Motion off under
 * prefers-reduced-motion.
 */
const FD_REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

class FeaturedDrop extends HTMLElement {
  #modal = null;
  #card = null;
  #closeBtn = null;
  #addBtn = null;
  #opened = false;
  #onScroll = null;
  #onExit = null;
  #onPointerMove = null;
  #timer = 0;
  #raf = 0;
  #px = 0;
  #py = 0;

  connectedCallback() {
    this.#modal = this.querySelector('[data-fd-modal]');
    if (!this.#modal) return;
    this.#card = this.querySelector('[data-fd-card]');
    this.#closeBtn = this.querySelector('[data-fd-close]');
    this.#addBtn = this.querySelector('[data-fd-add]');

    if (this.#alreadyShown()) return;

    this.#bindTriggers();
    this.#closeBtn?.addEventListener('click', () => this.close());
    this.#modal.addEventListener('click', (e) => {
      if (e.target === this.#modal) this.close();
    });
    document.addEventListener('keydown', this.#onKey);
    this.#addBtn?.addEventListener('click', this.#add);
  }

  disconnectedCallback() {
    this.#teardownTriggers();
    this.#unbindParallax();
    document.removeEventListener('keydown', this.#onKey);
  }

  #key() {
    return `fd-shown-${this.dataset.product || this.id}`;
  }

  #alreadyShown() {
    const freq = this.dataset.frequency || 'session';
    if (freq === 'always') return false;
    if (freq === 'session') return sessionStorage.getItem(this.#key()) != null;
    return localStorage.getItem(this.#key()) === new Date().toDateString(); // daily
  }

  #markShown() {
    const freq = this.dataset.frequency || 'session';
    if (freq === 'session') sessionStorage.setItem(this.#key(), '1');
    else if (freq === 'daily') localStorage.setItem(this.#key(), new Date().toDateString());
  }

  #bindTriggers() {
    const trigger = this.dataset.trigger || 'scroll';
    if (trigger === 'delay') {
      const ms = (parseFloat(this.dataset.delay || '6') || 6) * 1000;
      this.#timer = window.setTimeout(() => this.open(), ms);
      return;
    }
    if (trigger === 'exit') {
      this.#onExit = (e) => {
        if (e.clientY <= 0) this.open();
      };
      document.addEventListener('mouseout', this.#onExit);
      this.#bindScroll(0.6); // mobile fallback (no exit-intent on touch)
      return;
    }
    this.#bindScroll((parseFloat(this.dataset.scrollPercent || '40') || 40) / 100);
  }

  #bindScroll(threshold) {
    this.#onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= threshold) this.open();
    };
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  open() {
    if (this.#opened) return;
    this.#opened = true;
    this.#teardownTriggers();
    document.body.style.overflow = 'hidden';
    this.#modal.hidden = false;
    void this.#modal.offsetWidth; // reflow so the transition runs
    this.#modal.setAttribute('data-open', '');
    this.#closeBtn?.focus();
    this.#markShown();
    if (!FD_REDUCE.matches) this.#bindParallax();
  }

  close() {
    if (!this.#opened) return;
    this.#opened = false;
    this.#modal.removeAttribute('data-open');
    this.#unbindParallax();
    const modal = this.#modal;
    window.setTimeout(() => {
      modal.hidden = true;
      document.body.style.overflow = '';
    }, 260);
  }

  #onKey = (e) => {
    if (e.key === 'Escape' && this.#opened) this.close();
  };

  /* ---- 3D tilt / parallax ---- */
  #bindParallax() {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    this.#onPointerMove = (e) => {
      const r = this.#modal.getBoundingClientRect();
      this.#px = (e.clientX - r.left) / r.width - 0.5;
      this.#py = (e.clientY - r.top) / r.height - 0.5;
      if (!this.#raf) this.#raf = requestAnimationFrame(this.#applyTilt);
    };
    this.#modal.addEventListener('pointermove', this.#onPointerMove, { passive: true });
  }

  #unbindParallax() {
    if (this.#onPointerMove) this.#modal.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    if (this.#card) this.#card.style.transform = '';
  }

  #applyTilt = () => {
    this.#raf = 0;
    if (this.#card) {
      this.#card.style.transform = `rotateX(${(-this.#py * 8).toFixed(2)}deg) rotateY(${(this.#px * 10).toFixed(2)}deg)`;
    }
    this.#modal.style.setProperty('--fd-px', this.#px.toFixed(3));
    this.#modal.style.setProperty('--fd-py', this.#py.toFixed(3));
  };

  /* ---- Add to cart ---- */
  #add = async () => {
    const id = this.dataset.variantId;
    if (!id) {
      window.location.href = this.dataset.productUrl || '/';
      return;
    }
    this.#addBtn.setAttribute('aria-busy', 'true');
    this.#addBtn.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const body = { items: [{ id: Number(id), quantity: 1 }] };
    if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

    const url = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        this.#err(data.description || data.message || 'Could not add to cart.');
        return;
      }
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'featured-drop', { source: 'featured-drop', sections: data.sections })
      );
      this.close();
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (e) {
      this.#err('Something went wrong. Please try again.');
    } finally {
      this.#addBtn.removeAttribute('aria-busy');
      this.#addBtn.disabled = false;
    }
  };

  #err(message) {
    const el = this.querySelector('[data-fd-error]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
  }

  #teardownTriggers() {
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#onExit) document.removeEventListener('mouseout', this.#onExit);
    if (this.#timer) clearTimeout(this.#timer);
  }
}

if (!customElements.get('featured-drop')) {
  customElements.define('featured-drop', FeaturedDrop);
}
